import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useAuthenticationStatus } from '@nhost/react';
import { useNavigate } from 'react-router-dom';
import { LoadingScreen } from 'components/LoadingScreen';
import { Button } from 'components/Button';
import { withNotification } from 'components/Notification';
import { apiErrorHandler, NotificationTypes, SizeEnum } from 'utils';
import { GET_STORES } from 'gql/queries';
import { GET_KLAVIYO_INSTALL_URL } from 'gql/mutations';
import { beginShopifyReauthorization } from 'utils/shopifyReauthorization';

// The localStorage key Login.tsx checks after a successful sign-in to resume this flow —
// mirrors the existing `shipaid___post-install` convention (see Login.tsx's shopifySignupURI)
// rather than a query-param based return path, since a query param would have to survive an
// OAuth (Google) redirect round-trip too, and this app's login flow already solves that problem
// with localStorage for the post-install case.
export const KLAVIYO_INSTALL_RETURN_KEY = 'shipaid___klaviyo-install-pending';
const INSTALL_PATH = '/integrations/klaviyo/install';
const SHOPIFY_APP_STORE_URL =
  'https://apps.shopify.com/shipaid-shipping-protection';

// Entry point registered with Klaviyo as ShipAid's "OAuth installation URL" — what a merchant
// lands on after clicking Install from Klaviyo's own marketplace listing. Unlike the existing
// Connect-Klaviyo button inside Settings (which already knows the merchant's storeId from an
// active app session), a merchant arriving here may not be logged into ShipAid at all, and
// ShipAid's whole data model is per-store — so this page's only job is identity resolution
// (log in if needed, pick a store if ambiguous) before handing off to the exact same
// klaviyoInstallUrl mutation + OAuth redirect the existing Connect button already uses.
export const KlaviyoMarketplaceInstall = withNotification(
  ({ showNotification }: any) => {
    const navigate = useNavigate();
    const { isAuthenticated, isLoading: authLoading } =
      useAuthenticationStatus();
    const [selectedStoreId, setSelectedStoreId] = useState<string | null>(null);
    const [isShopifyRedirecting, setIsShopifyRedirecting] = useState(false);

    const { data: storesData, loading: storesLoading } = useQuery(GET_STORES, {
      skip: !isAuthenticated,
    });

    const [getInstallUrl, { loading: installUrlLoading }] = useMutation(
      GET_KLAVIYO_INSTALL_URL,
      {
        onCompleted(data) {
          const url = data?.klaviyoInstallUrl;
          if (url) {
            window.location.href = url;
          } else {
            showNotification(
              NotificationTypes.error,
              'Something went wrong connecting to Klaviyo, please try again.',
            );
          }
        },
        onError(error: any) {
          const newError = apiErrorHandler(error);
          showNotification(
            NotificationTypes.error,
            newError?.message ||
              'Something went wrong connecting to Klaviyo, please try again.',
          );
        },
      },
    );

    // Not logged in — remember we were headed here, then send the merchant to log in. Login.tsx
    // checks KLAVIYO_INSTALL_RETURN_KEY on success and bounces back to this exact page, same
    // pattern it already uses for the Shopify post-install redirect.
    useEffect(() => {
      if (!authLoading && !isAuthenticated) {
        localStorage.setItem(KLAVIYO_INSTALL_RETURN_KEY, INSTALL_PATH);
        navigate('/login');
      }
    }, [authLoading, isAuthenticated, navigate]);

    // Same convention as Home.tsx — a store the merchant has uninstalled ShipAid from shouldn't
    // be offered as a target for a brand-new Klaviyo connection.
    const stores = (storesData?.stores ?? []).filter(
      (store: any) => !store.uninstalledAt,
    );

    // Exactly one store — nothing to ask, just proceed straight to the OAuth handoff.
    useEffect(() => {
      if (isAuthenticated && !storesLoading && stores.length === 1) {
        localStorage.removeItem(KLAVIYO_INSTALL_RETURN_KEY);
        getInstallUrl({ variables: { storeId: stores[0].id } });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, storesLoading, stores.length]);

    const handleSelectStore = (storeId: string) => {
      setSelectedStoreId(storeId);
      localStorage.removeItem(KLAVIYO_INSTALL_RETURN_KEY);
      getInstallUrl({ variables: { storeId } });
    };

    if (authLoading || !isAuthenticated) return <LoadingScreen />;
    if (storesLoading) return <LoadingScreen />;

    if (!stores.length) {
      return (
        <div className="flex h-screen flex-col items-center justify-center gap-4 px-4 text-center">
          <h1 className="text-xl font-semibold text-[#0b1c30]">
            No ShipAid store found
          </h1>
          <p className="max-w-md text-sm text-[#64748b]">
            ShipAid must be installed on a Shopify store before it can connect
            to Klaviyo. Continue to Shopify and we&apos;ll bring you back here
            automatically.
          </p>
          <Button
            onClick={() => {
              setIsShopifyRedirecting(true);
              localStorage.removeItem(KLAVIYO_INSTALL_RETURN_KEY);
              beginShopifyReauthorization(
                SHOPIFY_APP_STORE_URL,
                INSTALL_PATH,
              );
            }}
            isLoading={isShopifyRedirecting}
            size={SizeEnum.md}
            color="primary"
            variant="contained"
          >
            Install ShipAid on Shopify
          </Button>
        </div>
      );
    }

    // More than one store and nothing picked yet — the only case that needs a real UI, since
    // Klaviyo's redirect here carries no context about which of the merchant's stores this
    // install is for.
    if (stores.length > 1 && !selectedStoreId) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-6 px-4 py-12">
          <div className="text-center">
            <h1 className="text-xl font-semibold text-[#0b1c30]">
              Which store is this Klaviyo connection for?
            </h1>
            <p className="mt-1 text-sm text-[#64748b]">
              Choose the ShipAid store you want to connect to this Klaviyo
              account.
            </p>
          </div>
          <div className="w-full max-w-sm space-y-2">
            {stores.map((store: any) => (
              <Button
                key={store.id}
                onClick={() => handleSelectStore(store.id)}
                size={SizeEnum.md}
                color="primary"
                variant="outlined"
                isLoading={installUrlLoading && selectedStoreId === store.id}
                disabled={installUrlLoading}
                className="w-full justify-center rounded-lg"
              >
                {store.name}
              </Button>
            ))}
          </div>
        </div>
      );
    }

    return <LoadingScreen />;
  },
);
